import React, { useRef, useState, useEffect, useCallback, useId } from 'react';

const DETECT_INTERVAL = 300;
const PHASE_TIMEOUT = 3500;

const PHASES = [
  { key: 'center', label: 'Nhìn thẳng vào camera' },
  { key: 'left', label: 'Từ từ quay khuôn mặt sang trái' },
  { key: 'right', label: 'Từ từ quay khuôn mặt sang phải' },
];

function createDetector() {
  if (typeof window !== 'undefined' && window.FaceDetector) {
    return new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
  }
  return null;
}

function getSupportedMimeType() {
  const types = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4'];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

export default function VideoCapture({ onCapture, onCancel }) {
  const maskId = `vc-${useId().replace(/:/g, '')}`;
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const machine = useRef({ idx: 0, startedAt: 0, finished: false });

  const [ready, setReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [activePhase, setActivePhase] = useState(-1);
  const [completed, setCompleted] = useState([false, false, false]);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    detectorRef.current = createDetector();

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (!mounted) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setReady(true);
      } catch {
        if (mounted) setError('Không thể truy cập camera. Vui lòng cấp quyền truy cập.');
      }
    })();

    return () => {
      mounted = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (tickRef.current) clearInterval(tickRef.current);
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    };
  }, []);

  const finishRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    recorder.onstop = () => {
      const mime = recorder.mimeType || 'video/webm';
      const ext = mime.includes('mp4') ? 'mp4' : 'webm';
      const blob = new Blob(chunksRef.current, { type: mime });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      onCapture(new File([blob], `liveness.${ext}`, { type: mime }));
    };
    recorder.stop();
  }, [onCapture]);

  useEffect(() => {
    if (!recording || done) return;

    const m = machine.current;
    m.idx = 0;
    m.startedAt = Date.now();
    m.finished = false;

    const advance = () => {
      if (m.finished) return;
      const doneIdx = m.idx;
      const nextIdx = doneIdx + 1;

      setCompleted((prev) => {
        const n = [...prev];
        n[doneIdx] = true;
        return n;
      });

      if (nextIdx >= PHASES.length) {
        m.finished = true;
        setDone(true);
        setActivePhase(-1);
        clearInterval(tickRef.current);
        setTimeout(finishRecording, 600);
        return;
      }

      m.idx = nextIdx;
      m.startedAt = Date.now();
      setActivePhase(nextIdx);
    };

    const tick = async () => {
      if (m.finished) return;
      const elapsed = Date.now() - m.startedAt;

      if (elapsed >= PHASE_TIMEOUT) {
        advance();
        return;
      }

      const video = videoRef.current;
      if (!detectorRef.current || !video || video.readyState < 4) return;

      try {
        const faces = await detectorRef.current.detect(video);
        if (faces.length === 0) return;

        const { x, width } = faces[0].boundingBox;
        const fcx = (x + width / 2) / video.videoWidth;
        const phase = PHASES[m.idx];
        let hit = false;

        if (phase.key === 'center') hit = fcx > 0.3 && fcx < 0.7;
        if (phase.key === 'left') hit = fcx > 0.6;
        if (phase.key === 'right') hit = fcx < 0.4;

        if (hit && elapsed > 800) advance();
      } catch { /* ignore */ }
    };

    setActivePhase(0);
    tickRef.current = setInterval(tick, DETECT_INTERVAL);
    return () => clearInterval(tickRef.current);
  }, [recording, done, finishRecording]);

  const startRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;

    chunksRef.current = [];
    const mime = getSupportedMimeType();
    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorderRef.current = recorder;
    recorder.start(100);
    setRecording(true);
  }, []);

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl bg-gray-900 p-8">
        <p className="text-sm text-red-400">{error}</p>
        <button type="button" onClick={onCancel} className="text-sm text-blue-400 underline">
          Quay lại
        </button>
      </div>
    );
  }

  const borderColor = done ? '#22c55e' : recording ? '#3b82f6' : '#6b7280';
  const currentPhase = activePhase >= 0 ? PHASES[activePhase] : null;

  return (
    <div className="overflow-hidden rounded-xl bg-black">
      <div className="relative">
        <video
          ref={videoRef}
          className="block w-full"
          muted
          playsInline
          style={{ transform: 'scaleX(-1)' }}
        />
        {ready && (
          <svg className="pointer-events-none absolute inset-0 h-full w-full">
            <defs>
              <mask id={maskId}>
                <rect width="100%" height="100%" fill="white" />
                <ellipse cx="50%" cy="45%" rx="28%" ry="38%" fill="black" />
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask={`url(#${maskId})`} />
            <ellipse
              cx="50%" cy="45%" rx="28%" ry="38%"
              fill="none"
              stroke={borderColor}
              strokeWidth="3"
              className="transition-all duration-500"
            />
          </svg>
        )}

        {currentPhase?.key === 'left' && (
          <div className="pointer-events-none absolute left-[5%] top-[38%] animate-pulse">
            <svg className="h-10 w-10 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
            </svg>
          </div>
        )}

        {currentPhase?.key === 'right' && (
          <div className="pointer-events-none absolute right-[5%] top-[38%] animate-pulse">
            <svg className="h-10 w-10 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
            </svg>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 pb-4 pt-10 text-center">
          {!ready && <p className="text-sm text-white">Đang khởi tạo camera...</p>}
          {ready && !recording && <p className="text-sm text-white">Nhấn nút để bắt đầu quay</p>}
          {recording && currentPhase && (
            <p className="text-sm font-medium text-white">{currentPhase.label}</p>
          )}
          {done && <p className="text-sm font-medium text-green-400">Hoàn thành!</p>}

          {recording && (
            <div className="mt-2 flex justify-center gap-1.5">
              {PHASES.map((p, i) => (
                <div
                  key={p.key}
                  className={`h-1.5 w-8 rounded-full transition-colors duration-300 ${
                    completed[i]
                      ? 'bg-green-400'
                      : i === activePhase
                        ? 'bg-blue-400'
                        : 'bg-gray-600'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-6 bg-gray-900 py-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full bg-gray-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-600"
        >
          Hủy
        </button>
        {!recording ? (
          <button
            type="button"
            onClick={startRecording}
            disabled={!ready}
            className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-red-400 transition-opacity disabled:opacity-40"
          >
            <div className="h-12 w-12 rounded-full bg-red-500" />
          </button>
        ) : (
          <div className="flex h-16 w-16 items-center justify-center">
            <div className="h-4 w-4 animate-pulse rounded-full bg-red-500" />
          </div>
        )}
        <div className="w-[84px]" />
      </div>
    </div>
  );
}
